import axios, { AxiosRequestConfig } from 'axios';
import moment from 'moment';
import { useRecaptcha } from 'store/store';
import { GetOrganizationUsersResponse } from './types';

export type User = {
  id: string;
  provider: string;
  providerId: string;
  username: string;
  name: string | null;
  createdAt: Date;
  updatedAt: Date;
  password: string | null;
  isVerified: boolean | null;
  roles: any;
  phone: string | null;
  email: string | null;
};

const api = () => {
  const _api = axios.create({
    baseURL: '/api',
  });

  _api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error.response &&
        error.response.data.message === 'Low recaptcha score.'
      ) {
        useRecaptcha.setState((state) => ({
          ...state,
          recaptchaFailed: true,
        }));
      }
      throw error.response.data;
    },
  );
  return _api;
};

declare const grecaptcha: any;

const getRecapthaToken = async (action: string) => {
  return new Promise<string>((resolve) => {
    grecaptcha.ready(() => {
      grecaptcha
        .execute(process.env.RECAPTCHA_SITE_KEY, { action })
        .then((token: string) => {
          resolve(token);
        });
    });
  });
};

const get =
  <T>(url: string, action?: string, config: AxiosRequestConfig = {}) =>
  async () => {
    return api().get<T>(url, {
      headers: action
        ? { recaptcha: await getRecapthaToken(action) }
        : undefined,
      ...config,
    });
  };

const _delete =
  <T>(url: string, action?: string) =>
  async () => {
    return api().delete<T>(url, {
      headers: action
        ? { recaptcha: await getRecapthaToken(action) }
        : undefined,
    });
  };

const post =
  <T>(url: string, action?: string) =>
  async (json: T) =>
    api().post(url, json, {
      headers: action
        ? { recaptcha: await getRecapthaToken(action) }
        : undefined,
    });

export const sendSpidipayOrder = post('/order/spidipay/initiate', 'order');
export const getSpidipayCheckoutResultOfOrder = () =>
  get(`/order/spidipay/result/`, 'order');

export const payMastercardOrder = post('/order/mastercard/pay', 'pay');
export const initiateMastercardOrder = post(
  '/order/mastercard/initiate',
  'orderInitiate',
);
export const authenticateMastercardOrder = post(
  '/order/mastercard/authenticate',
  'orderAuthenticate',
);

export const me = get<User>('/auth/jwt/profile');
export const registerLocal = post<{ values: any }>('/auth/local/register');
export const loginLocal = post<{ email: string; password: string }>(
  '/auth/local/login',
);
export const logout = post('/auth/jwt/logout');
export const verifyRegistration = post<{ token: string }>('/auth/local/verify');

export const getEvents = get('/events', 'events'); //FrontPage
export const getUserId = get('/user-id', 'userId');

export const getEvent = (id: string) => get(`/event/${id}`, 'event');

export const sendMtnRwandaOrder = post('/order/mtn-rwanda', 'order');
export const getMtnRwandaPaymentStatus = get(
  '/order/mtn-rwanda/payment-status',
  'order',
);

export const sendOrangeSonatelOrder = post('/order/orange-sonatel', 'order');
export const getOrangeSonatelPaymentStatus = get(
  '/order/orange-sonatel/payment-status',
  'order',
);

export const sendAirtelAfricaOrder = post('/order/airtelafrica', 'order');
export const getAirtelAfricaPaymentStatus = get(
  '/order/airtelafrica/payment-status',
  'order',
);

export const sendFreeSenegalOrder = post('/order/freesenegal', 'order');
export const getFreeSenegalPaymentStatus = get(
  '/order/freesenegal/payment-status',
  'order',
);

//admin
export const ticketGet = (id: string) => post('/admin/ticket-get', id);
export const ticketUse = (id: string) => post('/admin/ticket-use', id);

export const userGet = (id: string) => get(`/auth/local/user/${id}`, 'userGet');
export const userEdit = post<{ values: any }>('/auth/local/edit-user');
export const userDelete = (id: string) => {
  return _delete(`/auth/local/delete-user/${id}`);
};
export const organizationUsers = (id: number) => {
  return get<GetOrganizationUsersResponse>(`/admin/organization-users/${id}`);
};
//roles
export const usersWithOrganizationRoles = (id: number) => {
  return get<GetOrganizationUsersResponse>(`/admin/organization-roles/${id}`);
};
export const rolesEdit = post<{ values: any }>('/admin/edit-roles');

export const organizationsGet = get('/admin/organizations', 'organizationsGet');
export const organizationGet = (id: number) => {
  return get(`/admin/organization/${id}`, 'organizationsGet');
};

export const organizationEdit = post<{ values: any }>(
  '/admin/edit-organization',
);
export const organizationDelete = (id: number) => {
  return _delete(`/admin/delete-organization/${id}`);
};
// Ticket type
export const ticketTypesGet = (id: number) => {
  return get(`/admin/ticket-types/${id}`);
};

export const ticketTypeEdit = post<{ values: any }>('/admin/edit-ticket-type');

export const ticketTypeDelete = (id: number) => {
  return _delete(`/admin/delete-ticket-type/${id}`);
};

// Event type
export const eventTypesGet = (id: number) => {
  return get(`/admin/event-types/${id}`);
};

export const eventTypeEdit = post<{ values: any }>('/admin/edit-event-type');

export const eventTypeDelete = (id: number) => {
  return _delete(`/admin/delete-event-type/${id}`);
};

// Event
export const eventEditOptionsGet = (id: number) => {
  return get(`/admin/event-options/${id}`);
};

export const eventsGet = (
  id: number,
  dateRange: {
    start: Date;
    end: Date;
  },
) => {
  return get(
    `/admin/events/${id}?start=${moment(dateRange.start).format(
      'YYYYMMDD',
    )}&end=${moment(dateRange.end).format('YYYYMMDD')}`,
  );
};

export const adminEventGet = (id: number) => {
  return get(`/admin/event/${id}`);
};

export const eventEdit = post<{ values: any }>('/admin/edit-event');

export const eventDelete = (id: number) => {
  return _delete(`/admin/delete-event/${id}`);
};

//Ticket type set
export const ticketTypeSetsGet = (id: number) => {
  return get(`/admin/ticket-type-sets/${id}`);
};

export const ticketTypeSetEdit = post<{ values: any }>(
  '/admin/edit-ticket-type-set',
);

export const ticketTypeSetDelete = (id: number) => {
  return _delete(`/admin/delete-ticket-type-set/${id}`);
};

//Venues venues
export const venuesGet = (id: number) => {
  return get(`/admin/venues/${id}`);
};

export const venueGet = (id: string) => {
  return get(`/admin/venue/${id}`);
};

export const venueEdit = post<{ values: any }>('/admin/edit-venue');

export const venueDelete = (id: string) => {
  return _delete(`/admin/delete-venue/${id}`);
};

//venue part
export const venuePartEdit = post<{ values: any }>('/admin/edit-venue-part');

export const venuePartDelete = (id: string) => {
  return _delete(`/admin/delete-venue-part/${id}`);
};

export const ordersGet = (
  id: number,
  dateRange: {
    start: Date;
    end: Date;
  },
) => {
  return get(
    `/admin/orders/${id}?start=${moment(dateRange.start).format(
      'YYYYMMDD',
    )}&end=${moment(dateRange.end).format('YYYYMMDD')}`,
  );
};

export const summaryGet = (
  id: number,
  dateRange: {
    start: Date;
    end: Date;
  },
  venueId: string,
) => {
  return get(
    `/admin/summary/${id}?start=${moment(dateRange.start).format(
      'YYYYMMDD',
    )}&end=${moment(dateRange.end).format('YYYYMMDD')}&venueid=${venueId}`,
  );
};

export const getUserAdminOfOrganizations = get<
  {
    name: string;
    id: number;
  }[]
>('/admin/user-admin-of-organizations', 'getUserAdminOfOrganizations');
export const usersAdminGet = (organizationId: number) =>
  get(`/admin/users/${organizationId}`);
export const checkVeeziAccess = post<{
  veeziAccessToken: string;
  dataSource: string;
}>('/check-veezi-access');

export const eventLocationsGet = () => {
  return get(`/event-locations`);
};
export interface GetFilteredEventsOptions {
  start: Date;
  end: Date;
  categories: string[];
  location: string;
  latitude: number;
  longitude: number;
}

export const getFilteredEvents = (
  options: GetFilteredEventsOptions = {
    start: moment().toDate(),
    end: moment().endOf('day').toDate(),
    categories: [],
    location: 'Rwanda',
    latitude: 0,
    longitude: 0,
  },
) => {
  return get<any[]>('/events', 'events', {
    params: {
      start: options.start,
      end: options.end,
      categories: options.categories.join(','),
      location: options.location,
      latitude: options.latitude,
      longitude: options.longitude,
    },
  });
};
